import { useQuery } from "@apollo/client";
import { useState } from "react";
import {
  GetComplianceProgramTimelineQuery,
  GetComplianceProgramTimelineQueryVariables,
} from "types/graphql-schema";

import { ButtonSize } from "@components/button/button-constants";
import Loading from "@components/loading/loading";
import { ToggleButtonGroup } from "@components/toggle-button-group/toggle-button-group";
import { ToggleButtonGroupTheme } from "@components/toggle-button-group/toggle-button-group-constants";

import getComplianceProgramTimelineQuery from "../graphql/get-compliance-program-timeline-query";
import { useProgramsSection } from "../programs-section-context";
import ComplianceProgramMyTimeline from "./compliance-program-my-timeline";
import { ComplianceProgramProgression } from "./compliance-program-progression";
import { conditionalTimelineMessage } from "./compliance-program-timeline-vocabulary";

enum TimelineView {
  MySteps = "My steps",
  WholeReview = "Whole review",
}

/** The admin's view: every step and every send, for the whole roster. */
const WholeReviewTimeline = ({
  complianceProgramId,
}: {
  complianceProgramId: number;
}) => {
  const { data, loading, error } = useQuery<
    GetComplianceProgramTimelineQuery,
    GetComplianceProgramTimelineQueryVariables
  >(getComplianceProgramTimelineQuery, {
    fetchPolicy: "cache-and-network",
    variables: { complianceProgramId },
  });

  return (
    <ComplianceProgramProgression
      rows={data?.complianceProgram?.timeline ?? []}
      empty={
        loading ? (
          <Loading size="5" />
        ) : /* A failed request must not read as "this does nothing". */
        error ? (
          "This timeline could not be loaded. Reload the page to try again."
        ) : (
          "Nothing is scheduled yet. Set a start date and a due date to see the timeline."
        )
      }
    />
  );
};

/**
 * The Timeline tab of a review page.
 *
 * Everybody gets the **My steps** view: their own steps, with dates. Somebody who
 * can administer the review also gets **Whole review**, every step and message for
 * everyone, and lands on it, as the tab did before My steps existed. Only they see
 * the toggle; the whole review's query never runs for anybody else.
 *
 * `ongoing` and `kickoffAfterDays` come from the page's own query rather than from
 * this one, because an ongoing review has no dates of its own and the backend dates
 * the whole review's plan from today. Without them the rows below would read as days
 * the admin chose, which they are not. My steps needs no caption: it is dated from
 * the reader's own cycle.
 */
const ComplianceProgramTimeline = ({
  complianceProgramId,
  ongoing,
  kickoffAfterDays,
  canSeeWholeReview,
}: {
  complianceProgramId: number;
  ongoing: boolean;
  kickoffAfterDays: number;
  canSeeWholeReview: boolean;
}) => {
  const { noun } = useProgramsSection();
  const [chosenView, setChosenView] = useState<TimelineView>(
    canSeeWholeReview ? TimelineView.WholeReview : TimelineView.MySteps,
  );
  const view = canSeeWholeReview ? chosenView : TimelineView.MySteps;

  return (
    <div className="flex flex-col gap-4" aria-label={`${noun} timeline`}>
      <div className="flex items-start justify-between gap-4">
        {view === TimelineView.MySteps ? (
          <div className="flex flex-col gap-1">
            <div className="font-semibold text-gray-900">Your steps</div>
            <div className="text-sm text-gray-500">
              What you do, what others do about you, and when.
            </div>
          </div>
        ) : (
          <div className="text-sm text-gray-500">
            What this {noun.toLowerCase()} will do, and who each message
            reaches.
          </div>
        )}
        {canSeeWholeReview && (
          <ToggleButtonGroup
            aria-label="Timeline view"
            // The group's ring is drawn 1px outside it. Without the gap, the sticky
            // tab bar above paints over the ring's top edge.
            className="mt-0.5"
            theme={ToggleButtonGroupTheme.defaultButtons}
            size={ButtonSize.medium}
            buttons={[TimelineView.MySteps, TimelineView.WholeReview].map(
              (value) => ({
                title: value,
                value,
                active: value === view,
              }),
            )}
            onClick={({ value }) => value && setChosenView(value)}
          />
        )}
      </div>

      {ongoing && view === TimelineView.WholeReview && (
        <div className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900">
          {conditionalTimelineMessage(noun.toLowerCase(), kickoffAfterDays).map(
            (sentence) => (
              <p key={sentence} className="mt-1 first:mt-0">
                {sentence}
              </p>
            ),
          )}
        </div>
      )}

      {view === TimelineView.WholeReview ? (
        <WholeReviewTimeline complianceProgramId={complianceProgramId} />
      ) : (
        <ComplianceProgramMyTimeline
          complianceProgramId={complianceProgramId}
        />
      )}
    </div>
  );
};

export default ComplianceProgramTimeline;
